import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { reaction } from 'mobx';
import { ClipTypeEnum, Engine } from "@rendley/sdk";
import { ApplicationStore } from "../../../../store/ApplicationStore";
import { RendleyStore } from "../../../../store/RendleyStore";
import { TimelineStore } from "../../../../store/TimelineStore";
import { convertTimeToUnits } from "../../../../utils/dom/convertTimeToUnits";
import { convertUnitsToTime } from "../../../../utils/dom/convertUnitsToTime";
import { generateClipIdentifierClass } from "../../utils/generateClipIdentifierClass";
import { InteractiveClip, ResizeDirectionEnum } from "../../utils/InteractiveClip";
import { updateAdjacency } from "../../utils/updateLayerClipsAdjacency";
import { RendleyService } from "../../../../services/RendleyService";
import { ClipDragOverDividerEvent, TimelineEventsEnum } from "../../types/timeline.types";
import { CLIP_CLASSNAME, TIMELINE_TRACKS_CONTAINER_ID, TRACK_CLASSNAME } from "../../config/constants";
import '../Clip/Clip.styles.scss';
import {ContextMenuStore} from "../../../../store/ContextMenuStore";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import {DeleteIcon} from "../../../../../pages/NewHome/HomeIcons";
import {RangeIcon} from "../../../../assets/icons/RangeIcon";

const SNAP_THRESHOLD = 10;

interface LibrettoClipProps {
    librettoClipId: string;
    layerId: string;
    filename?: string;
    thumbnail?: string;
    isResizeEnabled?: boolean;
    backgroundColor?: string;
    children?: React.ReactNode;
}

export const LibrettoClip: React.FC<LibrettoClipProps> = observer(({ librettoClipId, layerId, filename = "Unknown", thumbnail, isResizeEnabled, backgroundColor = "43,98,253", children }) => {
    const [startTime, setStartTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [leftTrim, setLeftTrim] = useState(0);
    const [rightTrim, setRightTrim] = useState(0);
    const [trimmedDuration, setTrimmedDuration] = useState(0);
    const [width, setWidth] = useState(200);
    const [clipIds, setClipIds] = useState<string[]>([]);
    const [firstClipId, setFirstClipId] = useState<string | null>(null);
    const [lastClipId, setLastClipId] = useState<string | null>(null);

    const xRef = useRef(0);
    const yRef = useRef(0);

    const startTimeRef = useRef(0);
    const durationRef = useRef(0);
    const leftTrimRef = useRef(0);

    const [x, setX] = useState(0);
    const [y, setY] = useState(0);

    const [isFocused, setIsFocused] = useState(false);
    const [hasSuccessor, setHasSuccessor] = useState(false);
    const [hasPredecessor, setHasPredecessor] = useState(false);

    const elementRef = useRef<HTMLDivElement>(null);
    const interactionRef = useRef<InteractiveClip | null>(null);
    const initialPositionRef = useRef({ top: 0, height: 0, left: 0, width: 0, x: 0, clientXOffset: 0, clientYOffset: 0 });
    const layerSnapPositionsRef = useRef<{ top: number; height: number; layerId: string }[]>([]);
    const clipSnapPositionsRef = useRef<{ x: number; left: number; width: number; clipId: string }[]>([]);
    const focusEventRef = useRef<MouseEvent | null>(null);

    const clipStyle = backgroundColor ? {
        '--clip-background-color': backgroundColor
    } as React.CSSProperties : {};

    useEffect(() => {
        const disposer = reaction(
            () => ({
                librettoClipState: RendleyStore.getLibrettoClipState(librettoClipId)
            }),
            (payload) => {
                const { librettoClipState } = payload;
                
                if (!librettoClipState.combinedProperties) {
                    // No clips in this LibrettoClip
                    setClipIds([]);
                    setFirstClipId(null);
                    setLastClipId(null);
                    return;
                }

                const { clipIds, combinedProperties } = librettoClipState;

                const loadedClips = clipIds.map(id => RendleyStore.clips[id]).filter(Boolean);
                if (loadedClips.length !== clipIds.length) {
                    console.log(`[LibrettoClip] Waiting for ${clipIds.length - loadedClips.length} clips to load`);
                    return; // Don't update state until all clips are loaded
                }
                
                // Update clip references
                setClipIds(librettoClipState.clipIds);
                setFirstClipId(combinedProperties.firstClipId);
                setLastClipId(combinedProperties.lastClipId);
                
                // Update state from combined properties
                startTimeRef.current = combinedProperties.startTime;
                setStartTime(combinedProperties.startTime);
                durationRef.current = combinedProperties.duration;
                setDuration(combinedProperties.duration);
                leftTrimRef.current = combinedProperties.leftTrim;
                setLeftTrim(combinedProperties.leftTrim);
                setRightTrim(combinedProperties.rightTrim);
                setTrimmedDuration(combinedProperties.trimmedDuration);
                setHasPredecessor(combinedProperties.hasPredecessor);
                setHasSuccessor(combinedProperties.hasSuccessor);

                // Update positioning based on LibrettoClip properties
                xRef.current = convertTimeToUnits(combinedProperties.startTime + combinedProperties.leftTrim);
                setX(xRef.current);
                setWidth(convertTimeToUnits(combinedProperties.trimmedDuration));
                
                // Update adjacency
                updateAdjacency(layerId);
            },
            { fireImmediately: true }
        );
        
        return () => { disposer(); };
    }, [librettoClipId, layerId]);

    // Separate reaction for selection state (always works independently)
    useEffect(() => {
        const disposer = reaction(
            () => ApplicationStore.selectedLibrettoClipId,
            (selectedLibrettoClipId) => {
                const shouldBeFocused = librettoClipId &&
                    selectedLibrettoClipId &&
                    selectedLibrettoClipId === librettoClipId;
                setIsFocused(shouldBeFocused);
            },
            { fireImmediately: true }
        );
        
        return () => { disposer(); };
    }, [librettoClipId]);

    // Separate reaction for zoom changes
    useEffect(() => {
        const disposer = reaction(
            () => TimelineStore.zoom,
            () => {
                // Recalculate positions based on current zoom level
                const librettoClipState = RendleyStore.getLibrettoClipState(librettoClipId);
                if (!librettoClipState.combinedProperties) return;
                
                const { combinedProperties } = librettoClipState;
                xRef.current = convertTimeToUnits(combinedProperties.startTime + combinedProperties.leftTrim);
                setX(xRef.current);
                setWidth(convertTimeToUnits(combinedProperties.trimmedDuration));
            }
        );
        
        return () => { disposer(); };
    }, [librettoClipId]);

    useEffect(() => {
        if (elementRef.current == null || elementRef.current.parentElement?.parentElement == null || !firstClipId) return;
        interactionRef.current = new InteractiveClip(elementRef.current, elementRef.current.parentElement.parentElement, {
            onDragStart: dragStart,
            onResizeStart: resizeStart,
            onDrag: drag,
            onResize: resize,
            onResizeEnd: resizeEnd,
            onDragEnd: dragEnd,
        });
        handleUpdateStyles();
        return () => { interactionRef.current?.destroy(); };
    }, [firstClipId]);

    useEffect(() => {
        handleUpdateStyles();
    }, [hasPredecessor, hasSuccessor, width, xRef.current, yRef.current]);

    const handleFocusChange = () => {
        if (interactionRef.current == null) {
            setTimeout(() => { handleFocusChange(); });
            return;
        }
        if (isFocused && !ApplicationStore.rangeToolEnabled) {
            // @ts-ignore
            interactionRef.current.mount(focusEventRef.current, { isResizeEnabled });
        } else {
            interactionRef.current.destroy();
            focusEventRef.current = null;
        }
        if (elementRef.current != null) {
            elementRef.current.style.zIndex = isFocused ? "2" : "1";
        }
    };

    useEffect(() => { handleFocusChange(); }, [isFocused, ApplicationStore.rangeToolEnabled]);

    const handleUpdateStyles = () => {
        if (elementRef.current == null) return;
        if (hasPredecessor && hasSuccessor) {
            elementRef.current.style.width = `calc(${width}px - 2px)`;
            elementRef.current.style.transform = `translate(calc(${xRef.current}px + 1px), ${yRef.current}px)`;
        } else if (hasSuccessor) {
            elementRef.current.style.width = `calc(${width}px - 1px)`;
            elementRef.current.style.transform = `translate(${xRef.current}px, ${yRef.current}px)`;
        } else if (hasPredecessor) {
            elementRef.current.style.width = `calc(${width}px - 1px)`;
            elementRef.current.style.transform = `translate(calc(${xRef.current}px + 1px), ${yRef.current}px)`;
        } else {
            elementRef.current.style.width = `${width}px`;
            elementRef.current.style.transform = `translate(${xRef.current}px, ${yRef.current}px)`;
        }
        interactionRef.current?.moveable?.updateRect();
    };

    const drag = (clientX: number, clientY: number) => {
        const deltaX = clientX - initialPositionRef.current.left;
        let newX = initialPositionRef.current.x + deltaX - initialPositionRef.current.clientXOffset;
        let nextY = clientY - initialPositionRef.current.top - initialPositionRef.current.clientYOffset;
        const { dividerIndex, layerSnapPositionIndex } = findLayerSnapPosition(clientY);
        const currentClipLeft = newX;
        const currentClipRight = currentClipLeft + width;
        for (let i = 0; i < clipSnapPositionsRef.current.length; i++) {
            const { x: clipX, width: clipWidth } = clipSnapPositionsRef.current[i];
            const clipRight = clipX + clipWidth;
            const diffLeft = Math.abs(currentClipLeft - clipX);
            const diffRight = Math.abs(currentClipRight - clipRight);
            const diffRightLeft = Math.abs(currentClipRight - clipX);
            const diffLeftRight = Math.abs(currentClipLeft - clipRight);
            if (diffLeft <= SNAP_THRESHOLD) { newX = clipX; break; }
            else if (diffRight <= SNAP_THRESHOLD) { newX = clipRight - width; break; }
            else if (diffRightLeft <= SNAP_THRESHOLD) { newX = clipX - width; break; }
            else if (diffLeftRight <= SNAP_THRESHOLD) { newX = clipRight; break; }
        }
        if (layerSnapPositionIndex !== -1) {
            nextY = layerSnapPositionsRef.current[layerSnapPositionIndex].top - initialPositionRef.current.top;
            dispatchClipDragOverDividerEvent();
        }
        if (dividerIndex !== -1) {
            dispatchClipDragOverDividerEvent(layerSnapPositionsRef.current.length - dividerIndex);
        }
        yRef.current = nextY;
        setY(nextY);
        xRef.current = newX;
        setX(newX);
    };

    const resize = (clientX: number, direction: ResizeDirectionEnum) => {
        if (RendleyStore.clips?.[firstClipId]?.type === ClipTypeEnum.SUBTITLES) { return; }
        const MARGIN_PADDING = 6;
        let deltaX = 0;
        let newWidth = initialPositionRef.current.width;
        if (direction === ResizeDirectionEnum.LEFT) {
            deltaX = clientX - initialPositionRef.current.left - MARGIN_PADDING;
            newWidth = initialPositionRef.current.left + initialPositionRef.current.width - clientX + MARGIN_PADDING;
        } else if (direction === ResizeDirectionEnum.RIGHT) {
            newWidth = clientX - initialPositionRef.current.left + MARGIN_PADDING;
            deltaX = 0;
        }
        let newX = initialPositionRef.current.x + deltaX;
        const currentClipLeft = newX;
        const currentClipRight = currentClipLeft + newWidth;
        for (let i = 0; i < clipSnapPositionsRef.current.length; i++) {
            const { x: clipX, width: clipWidth } = clipSnapPositionsRef.current[i];
            const clipRight = clipX + clipWidth;
            const diffLeft = Math.abs(currentClipLeft - clipX);
            const diffRight = Math.abs(currentClipRight - clipRight);
            const diffRightLeft = Math.abs(currentClipRight - clipX);
            const diffLeftRight = Math.abs(currentClipLeft - clipRight);
            if (direction === ResizeDirectionEnum.LEFT) {
                if (diffLeft <= SNAP_THRESHOLD) { newX = clipX; newWidth = initialPositionRef.current.width + (initialPositionRef.current.x - newX); break; }
                else if (diffLeftRight <= SNAP_THRESHOLD) { newX = clipRight; newWidth = initialPositionRef.current.width + (initialPositionRef.current.x - newX); break; }
            } else if (direction === ResizeDirectionEnum.RIGHT) {
                if (diffRight <= SNAP_THRESHOLD) { newWidth = clipRight - currentClipLeft; break; }
                else if (diffRightLeft <= SNAP_THRESHOLD) { newWidth = clipX - currentClipLeft; break; }
            }
        }
        xRef.current = newX;
        setX(newX);
        setWidth(newWidth);
    };

    const resizeStart = () => {
        if (!firstClipId || RendleyStore.clips?.[firstClipId]?.type === ClipTypeEnum.SUBTITLES) { return; }
        if (!elementRef.current) { return; }
        const { top, height, left, width } = elementRef.current.getBoundingClientRect();
        initialPositionRef.current = { top, height, left, width, x: xRef.current, clientXOffset: 0, clientYOffset: 0 };
        TimelineStore.setDraggingLibrettoClip(librettoClipId, true);
        setClipSnapPositions();
    };

    const resizeEnd = (direction: ResizeDirectionEnum, distance: number) => {
        const firstClip = RendleyService.getClipById(firstClipId);
        const lastClip = RendleyService.getClipById(lastClipId);
        if (!firstClip || !firstClipId) return;
        if (RendleyStore.clips?.[firstClipId]?.type === ClipTypeEnum.SUBTITLES) { return; }
        const distanceToTime = convertUnitsToTime(distance);
        if (direction === ResizeDirectionEnum.LEFT) {
            firstClip.setLeftTrim(firstClip.getLeftTrim() - distanceToTime);
        } else if (direction === ResizeDirectionEnum.RIGHT) {
            lastClip?.setRightTrim(lastClip.getRightTrim() - distanceToTime);
        }
        handlePostUpdateClip();
        TimelineStore.setDraggingLibrettoClip(librettoClipId, false);
        clipSnapPositionsRef.current = [];
    };

    const dragStart = (clientX: number, clientY: number) => {
        if (!elementRef.current || !firstClipId) { return; }
        const { top, height, left, width } = elementRef.current.getBoundingClientRect();
        initialPositionRef.current = { top, height, left, width, x: xRef.current, clientXOffset: clientX - left, clientYOffset: clientY - top };
        TimelineStore.setDraggingLibrettoClip(librettoClipId, true);
        setLayerSnapPositions();
        setClipSnapPositions();
    };

    const dragEnd = (clientX: number, clientY: number) => {
        const { dividerIndex, layerSnapPositionIndex } = findLayerSnapPosition(clientY);

        // Calculate target layer and position
        let targetLayerId = layerId; // Default to current layer
        
        // Get the first clip to account for its left trim
        const firstClip = firstClipId ? Engine.getInstance().getClipById(firstClipId) : null;
        const firstClipLeftTrim = firstClip ? firstClip.getLeftTrim() : 0;
        
        // Calculate the actual start time (accounting for left trim)
        // and ensure it's never negative (snap to 0)
        let targetStartTime = Math.max(0, convertUnitsToTime(xRef.current));

        if (layerSnapPositionIndex !== -1) {
            // Moving to existing layer
            targetLayerId = layerSnapPositionsRef.current[layerSnapPositionIndex].layerId;
        } else if (dividerIndex !== -1) {
            // Creating new layer
            const newLayer = RendleyService.createLayer(layerSnapPositionsRef.current.length - dividerIndex);
            targetLayerId = newLayer.id;
        }

        // Reset visual state immediately
        yRef.current = 0;
        setY(0);

        // Now do ALL the actual movements atomically
        const currentClipIds = RendleyStore.getClipIdsForLibrettoClip(librettoClipId);
        PerformAtomicLibrettoClipMove(targetLayerId, targetStartTime, currentClipIds);

        // Clear drag state
        TimelineStore.setDraggingLibrettoClip(librettoClipId, false);
        layerSnapPositionsRef.current = [];
        clipSnapPositionsRef.current = [];
        dispatchClipDragOverDividerEvent(); // Clear any drag indicators
    };

    const dispatchClipDragOverDividerEvent = (index: number = -1) => {
        const event = new CustomEvent<ClipDragOverDividerEvent>(TimelineEventsEnum.CLIP_DRAG_OVER_DIVIDER, {
            detail: { clipId: firstClipId, index },
        });
        document.dispatchEvent(event);
    };

    const findLayerSnapPosition = (clientY: number) => {
        let dividerIndex = -1;
        let layerSnapPositionIndex = -1;
        if (!layerSnapPositionsRef.current.length) { return { dividerIndex, layerSnapPositionIndex }; }
        const firstSnapPosition = layerSnapPositionsRef.current[0];
        const lastSnapPosition = layerSnapPositionsRef.current[layerSnapPositionsRef.current.length - 1];
        if (clientY <= firstSnapPosition.top) { dividerIndex = 0; }
        if (clientY >= lastSnapPosition.top + lastSnapPosition.height) { dividerIndex = layerSnapPositionsRef.current.length; }
        if (dividerIndex > -1) { return { dividerIndex, layerSnapPositionIndex }; }
        for (let i = 0; i < layerSnapPositionsRef.current.length; i++) {
            const { top: trackTop, height: trackHeight } = layerSnapPositionsRef.current[i];
            const nextPosition = layerSnapPositionsRef.current[i + 1];
            if (clientY >= trackTop && clientY <= trackTop + trackHeight) { layerSnapPositionIndex = i; break; }
            if (clientY >= trackTop + trackHeight && clientY <= nextPosition?.top) { dividerIndex = i + 1; break; }
        }
        return { dividerIndex, layerSnapPositionIndex };
    };

    const setLayerSnapPositions = () => {
        const tracks = document.querySelectorAll(`.${TRACK_CLASSNAME}`);
        layerSnapPositionsRef.current = Array.from(tracks).map((track) => {
            // @ts-ignore
            const { top, height } = track.getBoundingClientRect();
            // @ts-ignore
            return { top, height, layerId: track.id };
        });
    };

    const setClipSnapPositions = () => {
        const clips = document.querySelectorAll(`.${CLIP_CLASSNAME}`);
        const tracksContainer = document.getElementById(TIMELINE_TRACKS_CONTAINER_ID);
        const offsetLeft = tracksContainer?.offsetLeft ?? 0;
        clipSnapPositionsRef.current = Array.from(clips)
            // @ts-ignore
            .filter((clip) => !clipIds.includes(clip.id))
            .map((clip) => {
                // @ts-ignore
                const { left, width } = clip.getBoundingClientRect();
                // @ts-ignore
                return { left, width, x: left - offsetLeft, clipId: clip.id };
            });
    };

    const handlePostUpdateClip = () => {
        const firstClip = Engine.getInstance().getClipById(firstClipId);
        if (!firstClip || !firstClipId) { return; }
        const newStartTime = convertUnitsToTime(xRef.current) - firstClip.getLeftTrim();
        const delta = newStartTime - firstClip.getStartTime();
        firstClip.setStartTime(newStartTime);
        startTimeRef.current = newStartTime;
        setStartTime(newStartTime);
        
        // Update all clips in the LibrettoClip
        clipIds.slice(1).forEach(id => {
            const c = Engine.getInstance().getClipById(id);
            if (c) {
                c.setStartTime(c.getStartTime() + delta);
                RendleyStore.updateClip(id, { startTime: c.getStartTime() });
            }
        });
        
        Engine.getInstance().getTimeline().adjustClipsLayout();
        RendleyStore.updateClip(firstClipId, { startTime: firstClip.getStartTime() });
        xRef.current = convertTimeToUnits(firstClip.getStartTime() + firstClip.getLeftTrim());
        setX(xRef.current);
        
        // Recalculate total duration
        const totalDur = clipIds.reduce((acc, id) => {
            const c = Engine.getInstance().getClipById(id);
            return c ? acc + c.getTrimmedDuration() : acc;
        }, 0);
        setWidth(convertTimeToUnits(totalDur));
        setTrimmedDuration(totalDur);
        
        TimelineStore.setDraggingLibrettoClip(librettoClipId, false);
        updateAdjacency(layerId);
    };

    const handleMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (isFocused) { return; }
        focusEventRef.current = event.nativeEvent;
        ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
    };

    const handleContextMenu = (e) => {
        e.preventDefault();
        
        const menuItems = [];

        // Check if there's a selected range
        if (ApplicationStore.selectedRange) {
            menuItems.push({
                title: "Remove selected range",
                icon: () => <RangeIcon/>,
                onClick: async () => {
                    const { start, end } = ApplicationStore.selectedRange;
                    await RendleyService.cutSegmentFromLibrettoClip(start, end);
                },
            });
        }

        menuItems.push({
            title: "Split at Playhead",
            icon: () => <ContentCutIcon />,
            onClick: () => { return RendleyService.splitLibrettoClipAtCurrentTime(); },
        });


        // Always show delete clip option
        menuItems.push({
            title: "Delete Clip",
            icon: () => <DeleteIcon />,
            onClick: () => { return RendleyService.deleteLibrettoClipById(librettoClipId); },
        });
        
        ContextMenuStore.show(e.clientX, e.clientY, menuItems);
    };

    const PerformAtomicLibrettoClipMove = async (targetLayerId: string, targetStartTime: number, currentClipIds: string[]) => {
        try {
            const moveOperations = [];
            let cumulativeTime = targetStartTime;

            for (const clipId of currentClipIds) {
                const clip = RendleyStore.clips[clipId];
                if (!clip) continue;

                moveOperations.push({
                    clipId: clipId,
                    newStartTime: cumulativeTime,
                    targetLayerId: targetLayerId
                });

                // Next clip starts where this one ends (contiguous)
                cumulativeTime += clip.trimmedDuration;
            }

            // Step 2: Move all clips to target layer (if different)
            if (targetLayerId !== layerId) {
                const movePromises = moveOperations.map(op =>
                    RendleyService.moveClipToLayer(op.clipId, op.targetLayerId)
                );
                await Promise.all(movePromises);
            }

            // Step 3: Update all clip positions
            moveOperations.forEach(op => {
                const rendleyClip = Engine.getInstance().getClipById(op.clipId);
                if (rendleyClip) {
                    // We want this clip to start (on the timeline) at op.newStartTime
                    // Thus we want rendleyClip.startTime + rendleyClip.leftTrim = op.newStartTime
                    const newStartTime = op.newStartTime - rendleyClip.getLeftTrim();
                    rendleyClip.setStartTime(newStartTime);
                }
            });

            // Step 4: Update LibrettoClip mappings (only if layer changed)
            if (targetLayerId !== layerId) {
                RendleyStore.removeLibrettoClipIdFromLayer(librettoClipId, layerId);
                RendleyStore.addLibrettoClipIdToLayer(librettoClipId, targetLayerId);
                RendleyStore.setLayerIdForLibrettoClip(librettoClipId, targetLayerId);
            }

            // Step 5: Trigger layout adjustment
            Engine.getInstance().getTimeline().adjustClipsLayout();
            updateAdjacency(targetLayerId);

            // Step 6: Update visual position to match actual position
            // const firstClip = Engine.getInstance().getClipById(firstClipId);
            // if (firstClip) {
            xRef.current = convertTimeToUnits(targetStartTime);
            setX(xRef.current);

            let verifyCumulative = targetStartTime;
            let index = 1;

            console.log("Found ", currentClipIds.length, "clips in perform atomic move function");

            for (const clipId of currentClipIds) {
                const rendleyClip = Engine.getInstance().getClipById(clipId);
                console.log("Clip ", index, " starts at ", rendleyClip.getStartTime() + rendleyClip.getLeftTrim(), " and ends at ", rendleyClip.getStartTime() + rendleyClip.getLeftTrim() + rendleyClip.getTrimmedDuration())
                index += 1
                if (rendleyClip) {
                    const newStartTime = verifyCumulative - rendleyClip.getLeftTrim();
                    if (Math.abs(rendleyClip.getStartTime() - newStartTime) > 0.01) {
                        rendleyClip.setStartTime(newStartTime);
                        console.warn(`LibrettoClip ${librettoClipId} clip ${clipId} start time mismatch: expected ${newStartTime}, got ${rendleyClip.getStartTime()}`);
                    }
                    verifyCumulative += rendleyClip.getTrimmedDuration();
                }
            }



        } catch (error) {
            console.error('Failed to move LibrettoClip:', error);
            // Reset to original position on error
            xRef.current = convertTimeToUnits(startTimeRef.current + leftTrimRef.current);
            setX(xRef.current);
        }
    };

    return (
        <div
            ref={elementRef}
            id={librettoClipId}
            data-libretto-clip-id={librettoClipId}
            style={clipStyle}
            className={clsx(CLIP_CLASSNAME, generateClipIdentifierClass(librettoClipId), {
                clip__focused: isFocused,
                clip__predecessor: hasPredecessor,
                clip__successor: hasSuccessor,
            })}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
        >
            <div className="clip__content">
                {thumbnail != null && (
                    <div className="clip__thumbnail-container" style={{ opacity: 0.5 }}>
                        <div className="clip__thumbnail" style={{ backgroundImage: `url(${thumbnail})` }} />
                    </div>
                )}
                {children}
                <div className="clip__content__text">
                    <b>{filename}</b>
                </div>
            </div>
        </div>
    );
});

export default LibrettoClip;
